import { useMemo, useState } from "react";
import { ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { ApiError } from "@/lib/api";
import { useListRouters } from "@/features/routers/api";
import { useListRedes } from "@/features/redes/api";
import { useMoverContrato } from "./api";
import type { ContratoDetail } from "./schemas";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  contrato: ContratoDetail;
  onMoved?: () => void;
};

/**
 * rc82 — Cambio de domicilio: reasigna el contrato a otro router + segmento.
 * Borra el secret del router viejo, toma nueva IP del segmento destino y recrea
 * el secret en el router nuevo (mismas credenciales PPPoE).
 */
export function MoverContratoDialog({ open, onOpenChange, contrato, onMoved }: Props) {
  const [routerId, setRouterId] = useState("");
  const [redId, setRedId] = useState("");
  const [motivo, setMotivo] = useState("");

  const { data: routersData } = useListRouters({ size: 200 });
  const { data: redesData } = useListRedes({ page: 1, size: 200 });
  const mover = useMoverContrato(contrato.id);

  const routers = routersData?.items ?? [];
  const routerActual = routers.find((r) => r.id === contrato.router_id);

  // Segmentos (redes) usables en el router destino: los atados a ese router
  // MÁS los globales (router_id null, usables en cualquier router). Sin uso interno.
  const redes = useMemo(
    () =>
      (redesData?.items ?? []).filter(
        (rd) => (!rd.router_id || rd.router_id === routerId) && !rd.uso_interno && rd.activo,
      ),
    [redesData, routerId],
  );
  const redSel = redes.find((r) => r.id === redId);

  const canSubmit = routerId && redId && motivo.trim().length >= 3 && !mover.isPending;

  function submit() {
    if (!canSubmit) return;
    mover.mutate(
      { nuevo_router_id: routerId, nueva_red_id: redId, motivo: motivo.trim() },
      {
        onSuccess: () => {
          toast.success("Traslado encolado", {
            description: "Se borra el secret del router anterior y se recrea en el nuevo con la IP asignada.",
          });
          onOpenChange(false);
          setRouterId(""); setRedId(""); setMotivo("");
          onMoved?.();
        },
        onError: (e) => {
          const err = e as unknown as ApiError;
          toast.error("No se pudo mover el contrato", { description: err?.message ?? "Error" });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Mover a otro router</DialogTitle>
          <DialogDescription>
            Reasigna el contrato a otro router (cambio de domicilio, mejor capacidad, etc.).
            Se conserva el usuario PPPoE; se toma una IP nueva del segmento elegido y se
            recrea el secret en el router destino.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* De → A */}
          <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs">
            <span className="text-muted-foreground">Actual:</span>
            <span className="font-medium">{routerActual?.nombre ?? "—"}</span>
            <span className="font-mono text-muted-foreground">{contrato.ip_asignada}</span>
            <ArrowRight size={14} className="mx-1 text-primary" />
            <span className="font-medium text-primary">
              {routers.find((r) => r.id === routerId)?.nombre ?? "elige destino"}
            </span>
            {redSel && <span className="font-mono text-muted-foreground">{redSel.cidr}</span>}
          </div>

          <div className="space-y-1.5">
            <Label>Router destino</Label>
            <Select
              value={routerId}
              onChange={(e) => { setRouterId(e.target.value); setRedId(""); }}
            >
              <option value="">Selecciona un router…</option>
              {routers
                .filter((r) => r.id !== contrato.router_id)
                .map((r) => (
                  <option key={r.id} value={r.id}>{r.nombre}</option>
                ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Segmento (red) — de aquí se toma la nueva IP</Label>
            <Select value={redId} onChange={(e) => setRedId(e.target.value)} disabled={!routerId}>
              <option value="">
                {!routerId ? "Elige router primero…" : redes.length ? "Selecciona segmento…" : "Este router no tiene segmentos"}
              </option>
              {redes.map((rd) => (
                <option key={rd.id} value={rd.id}>{rd.nombre} · {rd.cidr}</option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Motivo</Label>
            <Textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={2}
              placeholder="Ej. Cambio de domicilio del cliente a zona San Pablito"
            />
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-2xs text-amber-600 dark:text-amber-400">
            <Info size={13} className="mt-0.5 flex-none" />
            <span>
              El servicio del cliente se interrumpe unos segundos mientras se recrea el secret
              en el router nuevo. La IP de gestión del CPE se actualiza para que ByteNMS lo siga.
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mover.isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {mover.isPending ? "Moviendo…" : "Mover contrato"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
